<template>
  <div class="archery-results-container">
    <div class="results-card">
      <!-- Results Header -->
      <div class="results-header">
        <h3 class="results-title">Archery Results</h3>
        <span class="participant-count">{{ participants.length }} {{ participants.length === 1 ? 'archer' : 'archers' }}</span>
      </div>

      <!-- Results Table -->
      <div class="results-table">
        <!-- Table Header -->
        <div class="table-header">
          <div class="col-position">POS</div>
          <div class="col-archer">ARCHER</div>
          <div class="col-10x">10+X</div>
          <div class="col-x">X</div>
          <div class="col-total">TOTAL</div>
          <div class="col-expand"></div>
        </div>

        <!-- Table Body -->
        <div class="table-body">
          <div
            class="table-row"
            v-for="(participant, index) in sortedParticipants"
            :key="participant.id"
          >
            <!-- Main Row -->
            <div class="main-row" :class="getRowClass(index + 1)">
              <!-- Position -->
              <div class="col-position">
                <span class="position-badge" :class="getPositionClass(index + 1)">
                  {{ participant.position || index + 1 }}
                </span>
              </div>

              <!-- Archer Name -->
              <div class="col-archer">
                <div class="archer-info">
                  <span class="archer-name">{{ getParticipantName(participant) }}</span>
                  <span v-if="participant.number" class="archer-number">{{ participant.number }}</span>
                </div>
              </div>

              <!-- 10+X Count -->
              <div class="col-10x">
                <span class="score-value">{{ get10PlusXCount(participant) }}</span>
              </div>

              <!-- X Count -->
              <div class="col-x">
                <span class="score-value">{{ getXCount(participant) }}</span>
              </div>

              <!-- Total Score -->
              <div class="col-total">
                <span class="total-score" :class="getTotalScoreClass(participant)">
                  {{ getTotalScore(participant) }}
                </span>
              </div>

              <!-- Expand Button -->
              <div class="col-expand">
                <button
                  class="expand-btn"
                  @click="toggleExpanded(participant.id)"
                  :class="{ expanded: expandedRows.includes(participant.id) }"
                >
                  <ion-icon
                    :icon="expandedRows.includes(participant.id) ? chevronUpOutline : chevronDownOutline"
                  />
                </button>
              </div>
            </div>

            <!-- Expanded Details -->
            <div
              class="expanded-details"
              v-if="expandedRows.includes(participant.id)"
            >
              <div class="arrows-header">
                <span class="arrows-label">ARROWS</span>
                <span class="arrows-numbers">
                  <span v-for="i in getArrowCount(participant)" :key="i" class="arrow-number">{{ i }}</span>
                </span>
                <span class="sum-label">SUM</span>
                <span class="10x-label">10+X</span>
                <span class="x-label">X</span>
                <span class="total-label">TOTAL</span>
              </div>

              <div class="arrows-details">
                <div
                  class="arrow-end"
                  v-for="(end, endIndex) in getArrowEnds(participant)"
                  :key="endIndex"
                >
                  <span class="end-number">{{ endIndex + 1 }}</span>
                  <div class="arrow-scores">
                    <span
                      v-for="(arrow, arrowIndex) in end.arrows"
                      :key="arrowIndex"
                      class="arrow-score"
                      :class="getArrowScoreClass(arrow)"
                    >
                      {{ formatArrowScore(arrow) }}
                    </span>
                  </div>
                  <span class="end-sum">{{ end.sum }}</span>
                  <span class="end-10x">{{ end.tenPlusX }}</span>
                  <span class="end-x">{{ end.x }}</span>
                  <span class="end-total">{{ end.total }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue';
import { IonIcon } from '@ionic/vue';
import { chevronDownOutline, chevronUpOutline } from 'ionicons/icons';

export default defineComponent({
  name: 'ArcheryResults',
  components: {
    IonIcon
  },
  props: {
    participants: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  setup() {
    const expandedRows = ref([]);

    const toggleExpanded = (participantId) => {
      const index = expandedRows.value.indexOf(participantId);
      if (index > -1) {
        expandedRows.value.splice(index, 1);
      } else {
        expandedRows.value.push(participantId);
      }
    };

    return {
      expandedRows,
      toggleExpanded,
      chevronDownOutline,
      chevronUpOutline
    };
  },
  computed: {
    sortedParticipants() {
      return [...this.participants].sort((a, b) => {
        // Sort by total score (highest first) if available
        const totalA = this.getTotalScore(a);
        const totalB = this.getTotalScore(b);

        if (totalA !== totalB && totalA !== '-' && totalB !== '-') {
          return parseInt(totalB) - parseInt(totalA);
        }

        // Fallback to position-based sorting
        if (a.position && b.position) {
          return a.position - b.position;
        }
        return 0;
      });
    }
  },
  methods: {
    getParticipantName(participant) {
      if (participant.member && participant.member.profile) {
        return `${participant.member.profile.firstName} ${participant.member.profile.lastName}`;
      }
      if (participant.member) {
        return `${participant.member.firstName || ''} ${participant.member.lastName || ''}`.trim() ||
               participant.member.name || 'Unknown Member';
      }
      return participant.name || 'Unknown Archer';
    },

    getTotalScore(participant) {
      // Calculate total from archeryScores if available
      if (participant.archeryScores) {
        let total = 0;
        Object.values(participant.archeryScores).forEach(end => {
          if (end.arrows && Array.isArray(end.arrows)) {
            end.arrows.forEach(arrow => {
              const score = this.parseArrowScore(arrow);
              if (score > 0) total += score;
            });
          }
        });
        return total > 0 ? total : '-';
      }

      // Legacy format support
      if (participant.arrows && Array.isArray(participant.arrows)) {
        let total = 0;
        participant.arrows.forEach(end => {
          if (end.arrows && Array.isArray(end.arrows)) {
            end.arrows.forEach(arrow => {
              const score = this.parseArrowScore(arrow);
              if (score > 0) total += score;
            });
          }
        });
        return total > 0 ? total : '-';
      }

      // Fallback to participant points/score
      return participant.points || participant.score || participant.total || '-';
    },

    get10PlusXCount(participant) {
      // Calculate from archeryScores if available
      if (participant.archeryScores) {
        let count = 0;
        Object.values(participant.archeryScores).forEach(end => {
          if (end.arrows && Array.isArray(end.arrows)) {
            end.arrows.forEach(arrow => {
              const score = this.parseArrowScore(arrow);
              if (score >= 10) count++;
            });
          }
        });
        return count;
      }

      // Legacy format support
      if (participant.arrows && Array.isArray(participant.arrows)) {
        let count = 0;
        participant.arrows.forEach(end => {
          if (end.arrows && Array.isArray(end.arrows)) {
            end.arrows.forEach(arrow => {
              const score = this.parseArrowScore(arrow);
              if (score >= 10) count++;
            });
          }
        });
        return count;
      }
      return participant.tenPlusX || 0;
    },

    getXCount(participant) {
      // Calculate from archeryScores using xValues array
      if (participant.archeryScores) {
        let count = 0;
        Object.values(participant.archeryScores).forEach(end => {
          if (end.xValues && Array.isArray(end.xValues)) {
            count += end.xValues.filter(x => x === true).length;
          }
        });
        return count;
      }

      // Legacy format support
      if (participant.arrows && Array.isArray(participant.arrows)) {
        let count = 0;
        participant.arrows.forEach(end => {
          if (end.arrows && Array.isArray(end.arrows)) {
            end.arrows.forEach(arrow => {
              const score = this.parseArrowScore(arrow);
              if (score === 10 && (arrow.toString().toLowerCase().includes('x') || arrow === 'X')) {
                count++;
              }
            });
          }
        });
        return count;
      }
      return participant.x || 0;
    },

    parseArrowScore(arrow) {
      if (arrow === 'X' || arrow === 'x') return 10;
      if (arrow === 'M' || arrow === 'm') return 0;
      const score = parseInt(arrow);
      return isNaN(score) ? 0 : score;
    },

    formatArrowScore(arrow) {
      if (arrow === 'X' || arrow === 'x') return 'X';
      if (arrow === 'M' || arrow === 'm') return 'M';
      return arrow.toString();
    },

    getArrowScoreClass(arrow) {
      const score = this.parseArrowScore(arrow);
      if (arrow === 'X' || arrow === 'x') return 'arrow-x';
      if (score === 10) return 'arrow-10';
      if (score >= 8) return 'arrow-high';
      if (score >= 6) return 'arrow-medium';
      if (score > 0) return 'arrow-low';
      return 'arrow-miss';
    },

    getArrowCount(participant) {
      // Get arrows per end from archery scores data
      if (participant.archeryScores) {
        const firstEnd = Object.values(participant.archeryScores)[0];
        if (firstEnd && firstEnd.arrows) {
          return firstEnd.arrows.length;
        }
      }
      // Fallback to standard archery (6 arrows per end for target, 3 for field)
      return participant.arrowsPerEnd || 6;
    },

    getArrowEnds(participant) {
      // Handle archeryScores format
      if (participant.archeryScores) {
        const ends = [];
        const sortedEndKeys = Object.keys(participant.archeryScores).sort((a, b) => parseInt(a) - parseInt(b));
        let runningTotal = 0;

        sortedEndKeys.forEach((endKey, index) => {
          const end = participant.archeryScores[endKey];
          const arrows = end.arrows || [];
          const xValues = end.xValues || [];

          // Create display arrows with X markers
          const displayArrows = arrows.map((arrow, i) => {
            if (xValues[i] && arrow === 10) return 'X';
            return arrow;
          });

          const sum = arrows.reduce((total, arrow) => total + this.parseArrowScore(arrow), 0);
          const tenPlusX = arrows.filter(arrow => this.parseArrowScore(arrow) >= 10).length;
          const x = xValues.filter(x => x === true).length;

          runningTotal += sum;

          ends.push({
            arrows: displayArrows,
            sum,
            tenPlusX,
            x,
            total: runningTotal
          });
        });

        return ends;
      }

      // Legacy format support
      if (participant.arrows && Array.isArray(participant.arrows)) {
        return participant.arrows.map((end, endIndex) => {
          const arrows = end.arrows || [];
          const sum = arrows.reduce((total, arrow) => total + this.parseArrowScore(arrow), 0);
          const tenPlusX = arrows.filter(arrow => this.parseArrowScore(arrow) >= 10).length;
          const x = arrows.filter(arrow => arrow === 'X' || arrow === 'x').length;

          // Calculate running total
          let runningTotal = 0;
          for (let i = 0; i <= endIndex; i++) {
            if (participant.arrows[i] && participant.arrows[i].arrows) {
              runningTotal += participant.arrows[i].arrows
                .reduce((total, arrow) => total + this.parseArrowScore(arrow), 0);
            }
          }

          return {
            arrows,
            sum,
            tenPlusX,
            x,
            total: runningTotal
          };
        });
      }

      // Generate sample data if no arrows data available
      return [];
    },

    getTotalScoreClass(participant) {
      const total = this.getTotalScore(participant);
      if (total === '-') return 'no-score';
      const score = parseInt(total);
      if (score >= 580) return 'excellent';
      if (score >= 550) return 'good';
      return 'average';
    },

    getPositionClass(position) {
      if (position === 1) return 'gold';
      if (position === 2) return 'silver';
      if (position === 3) return 'bronze';
      return 'default';
    },

    getRowClass(position) {
      if (position <= 3) return `podium-${position}`;
      return '';
    }
  }
});
</script>

<style lang="scss" scoped>
.archery-results-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem;
}

.results-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;

  .results-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    color: #111827;
  }

  .participant-count {
    color: #6b7280;
    font-size: 0.75rem;
    font-weight: 400;
  }
}

.results-table {
  .table-header {
    display: grid;
    grid-template-columns: 50px 1fr 60px 50px 80px 40px;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    font-weight: 500;
    color: #6b7280;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    align-items: center;
    background: #f9fafb;
  }

  .table-body {
    .table-row {
      border-bottom: 1px solid #f3f4f6;

      &:last-child {
        border-bottom: none;
      }

      .main-row {
        display: grid;
        grid-template-columns: 50px 1fr 60px 50px 80px 40px;
        gap: 0.75rem;
        padding: 0.75rem 1rem;
        transition: background 0.1s ease;
        align-items: center;

        &:hover {
          background: #f9fafb;
        }

        &.podium-1 {
          background: #fffbeb;
          border-left: 2px solid #f59e0b;
        }

        &.podium-2 {
          background: #f8fafc;
          border-left: 2px solid #9ca3af;
        }

        &.podium-3 {
          background: #fef2f2;
          border-left: 2px solid #ea580c;
        }
      }
    }
  }
}

.col-position {
  .position-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 4px;
    font-weight: 600;
    font-size: 0.8rem;

    &.gold {
      background: #f59e0b;
      color: white;
    }

    &.silver {
      background: #9ca3af;
      color: white;
    }

    &.bronze {
      background: #ea580c;
      color: white;
    }

    &.default {
      background: #f3f4f6;
      color: #6b7280;
    }
  }
}

.col-archer {
  .archer-info {
    .archer-name {
      display: block;
      font-size: 0.85rem;
      font-weight: 500;
      color: #111827;
      line-height: 1.2;
    }

    .archer-number {
      display: block;
      font-size: 0.7rem;
      color: #9ca3af;
      margin-top: 0.1rem;
      font-weight: 400;
    }
  }
}

.col-10x, .col-x {
  .score-value {
    font-size: 0.85rem;
    font-weight: 500;
    color: #374151;
    text-align: center;
  }
}

.col-total {
  .total-score {
    font-size: 1rem;
    font-weight: 600;
    text-align: right;

    &.excellent {
      color: #059669;
    }

    &.good {
      color: #0891b2;
    }

    &.average {
      color: #374151;
    }

    &.no-score {
      color: #9ca3af;
    }
  }
}

.col-expand {
  .expand-btn {
    background: transparent;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 0.25rem;
    cursor: pointer;
    transition: all 0.1s ease;
    color: #9ca3af;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #f9fafb;
      color: #6b7280;
      border-color: #d1d5db;
    }

    &.expanded {
      background: #f3f4f6;
      color: #374151;
      border-color: #d1d5db;
    }

    ion-icon {
      font-size: 0.875rem;
    }
  }
}

.expanded-details {
  background: #fafafa;
  border-top: 1px solid #e5e7eb;
  padding: 0.75rem 1rem;

  .arrows-header {
    display: grid;
    grid-template-columns: 60px 1fr 50px 40px 40px 60px;
    gap: 0.5rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid #e5e7eb;
    font-weight: 500;
    color: #6b7280;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    align-items: center;
    text-align: center;

    .arrows-numbers {
      display: flex;
      gap: 2rem;
      justify-content: center;

      .arrow-number {
        width: 1.25rem;
        height: 1.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #f3f4f6;
        border-radius: 2px;
        font-size: 0.65rem;
        font-weight: 500;
        color: #9ca3af;
      }
    }
  }

  .arrows-details {
    .arrow-end {
      display: grid;
      grid-template-columns: 60px 1fr 50px 40px 40px 60px;
      gap: 0.5rem;
      padding: 0.5rem 0;
      border-bottom: 1px solid #f3f4f6;
      align-items: center;

      &:last-child {
        border-bottom: none;
      }

      .end-number {
        font-weight: 500;
        color: #6b7280;
        text-align: center;
        font-size: 0.75rem;
      }

      .arrow-scores {
        display: flex;
        gap: 2rem;
        justify-content: center;

        .arrow-score {
          width: 1.25rem;
          height: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 2px;
          font-size: 0.7rem;
          font-weight: 500;
          border: 1px solid;

          &.arrow-x {
            background: #fef2f2;
            border-color: #fca5a5;
            color: #dc2626;
          }

          &.arrow-10 {
            background: #f0fdf4;
            border-color: #bbf7d0;
            color: #059669;
          }

          &.arrow-high {
            background: #f0f9ff;
            border-color: #93c5fd;
            color: #0891b2;
          }

          &.arrow-medium {
            background: #fffbeb;
            border-color: #fcd34d;
            color: #f59e0b;
          }

          &.arrow-low {
            background: #f9fafb;
            border-color: #d1d5db;
            color: #6b7280;
          }

          &.arrow-miss {
            background: #fef2f2;
            border-color: #fca5a5;
            color: #ef4444;
          }
        }
      }

      .end-sum, .end-10x, .end-x {
        font-weight: 500;
        color: #6b7280;
        text-align: center;
        font-size: 0.75rem;
      }

      .end-total {
        font-size: 0.85rem;
        font-weight: 600;
        color: #374151;
        text-align: center;
      }
    }
  }
}

// Mobile Responsiveness
@media (max-width: 768px) {
  .archery-results-container {
    padding: 0.25rem;
  }

  .results-header {
    padding: 0.75rem 1rem;

    .results-title {
      font-size: 1rem;
    }

    .participant-count {
      font-size: 0.7rem;
    }
  }

  .results-table {
    .table-header {
      grid-template-columns: 40px 1fr 40px 30px 60px 30px;
      padding: 0.5rem 0.75rem;
      gap: 0.5rem;
      font-size: 0.65rem;
    }

    .table-body .table-row .main-row {
      grid-template-columns: 40px 1fr 40px 30px 60px 30px;
      padding: 0.5rem 0.75rem;
      gap: 0.5rem;
    }
  }

  .col-position .position-badge {
    width: 1.5rem;
    height: 1.5rem;
    font-size: 0.7rem;
  }

  .col-archer .archer-info .archer-name {
    font-size: 0.8rem;
  }

  .col-archer .archer-info .archer-number {
    font-size: 0.65rem;
  }

  .col-10x, .col-x {
    .score-value {
      font-size: 0.75rem;
    }
  }

  .col-total .total-score {
    font-size: 0.9rem;
  }

  .col-expand .expand-btn {
    width: 20px;
    height: 20px;

    ion-icon {
      font-size: 0.75rem;
    }
  }

  .expanded-details {
    padding: 0.5rem;

    .arrows-header {
      grid-template-columns: 45px 1fr 35px 25px 25px 45px;
      gap: 0.25rem;
      font-size: 0.6rem;

      .arrows-numbers {
        gap: 0.8rem;

        .arrow-number {
          width: 1rem;
          height: 1rem;
          font-size: 0.6rem;
        }
      }
    }

    .arrows-details .arrow-end {
      grid-template-columns: 45px 1fr 35px 25px 25px 45px;
      gap: 0.25rem;

      .end-number {
        font-size: 0.7rem;
      }

      .arrow-scores {
        gap: 0.8rem;

        .arrow-score {
          width: 1rem;
          height: 1rem;
          font-size: 0.65rem;
        }
      }

      .end-sum, .end-10x, .end-x {
        font-size: 0.7rem;
      }

      .end-total {
        font-size: 0.75rem;
      }
    }
  }
}
</style>